import { useTranslation } from 'react-i18next';

import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import ErrorIcon from '@mui/icons-material/Error';
import SignalWifiStatusbarConnectedNoInternet4Icon from '@mui/icons-material/SignalWifiStatusbarConnectedNoInternet4';
import WarningIcon from '@mui/icons-material/Warning';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { Box, Button, Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/joy';

// Mock data for alerts
const mockAlerts = [
  {
    id: 'alert-001',
    severity: 'critical',
    title: 'Connection Lost',
    description: 'Drone Alpha has lost connection for more than 5 minutes',
    time: '10:45 AM',
    icon: 'connection'
  },
  {
    id: 'alert-002',
    severity: 'critical',
    title: 'Low Battery',
    description: 'Drone Beta battery level critical at 5%',
    time: '10:30 AM',
    icon: 'battery'
  },
  {
    id: 'alert-003',
    severity: 'warning',
    title: 'High Wind Speed',
    description: 'Wind speed exceeding safe flight parameters at Site A',
    time: '10:15 AM',
    icon: 'weather'
  },
  {
    id: 'alert-004',
    severity: 'warning',
    title: 'GPS Signal Weak',
    description: 'Drone Gamma reporting weak GPS signal strength',
    time: '10:00 AM',
    icon: 'gps'
  }
];

interface CriticalAlertsPanelProps {
  alerts?: typeof mockAlerts;
}

const CriticalAlertsPanel = ({ alerts = mockAlerts }: CriticalAlertsPanelProps) => {
  const { t } = useTranslation();

  // Function to get the appropriate icon for each alert type
  const getAlertIcon = (iconType: string) => {
    switch (iconType) {
      case 'connection':
        return <WifiOffIcon fontSize='small' />;
      case 'battery':
        return <BatteryAlertIcon fontSize='small' />;
      case 'weather':
        return <WarningIcon fontSize='small' />;
      case 'gps':
        return <SignalWifiStatusbarConnectedNoInternet4Icon fontSize='small' />;
      default:
        return <ErrorIcon fontSize='small' />;
    }
  };

  // Filter critical alerts
  const criticalAlerts = alerts.filter((alert) => alert.severity === 'critical');
  const warningAlerts = alerts.filter((alert) => alert.severity === 'warning');

  return (
    <Card variant='outlined'>
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography level='title-md'>{t('dashboard.critical_alerts', 'Critical Alerts')}</Typography>
        <Chip color='danger' size='sm' variant='soft'>
          {criticalAlerts.length} {t('dashboard.critical', 'Critical')}
        </Chip>
      </Box>
      <CardContent>
        <Stack divider={<Divider />} spacing={2}>
          {criticalAlerts.map((alert) => (
            <Box key={alert.id}>
              <Stack alignItems='flex-start' direction='row' spacing={1}>
                <Box
                  sx={{
                    color: 'danger.500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mt: 0.5
                  }}
                >
                  {getAlertIcon(alert.icon)}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Stack alignItems='flex-start' direction='row' justifyContent='space-between'>
                    <Typography color='danger' level='title-sm'>
                      {alert.title}
                    </Typography>
                    <Typography color='neutral' level='body-xs'>
                      {alert.time}
                    </Typography>
                  </Stack>
                  <Typography level='body-sm'>{alert.description}</Typography>
                </Box>
              </Stack>
            </Box>
          ))}

          {warningAlerts.length > 0 && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography fontWeight='bold' level='body-sm'>
                  {t('dashboard.warnings', 'Warnings')}
                </Typography>
                <Chip color='warning' size='sm' variant='soft'>
                  {warningAlerts.length}
                </Chip>
              </Box>

              {warningAlerts.map((alert) => (
                <Box key={alert.id}>
                  <Stack alignItems='flex-start' direction='row' spacing={1}>
                    <Box
                      sx={{
                        color: 'warning.500',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mt: 0.5
                      }}
                    >
                      {getAlertIcon(alert.icon)}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Stack alignItems='flex-start' direction='row' justifyContent='space-between'>
                        <Typography color='warning' level='title-sm'>
                          {alert.title}
                        </Typography>
                        <Typography color='neutral' level='body-xs'>
                          {alert.time}
                        </Typography>
                      </Stack>
                      <Typography level='body-sm'>{alert.description}</Typography>
                    </Box>
                  </Stack>
                </Box>
              ))}
            </>
          )}
        </Stack>

        <Button color='neutral' fullWidth sx={{ mt: 2 }} variant='outlined'>
          {t('dashboard.view_all_alerts', 'View All Alerts')}
        </Button>
      </CardContent>
    </Card>
  );
};

export default CriticalAlertsPanel;
